import React from 'react';
import {Box, Typography, Container, Grid, alpha, useTheme} from '@mui/material';
import {useTranslation} from "react-i18next";
import RestaurantIcon from '@mui/icons-material/Restaurant';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LocalCafeIcon from '@mui/icons-material/LocalCafe';
import IcecreamIcon from '@mui/icons-material/Icecream';
import BakeryDiningIcon from '@mui/icons-material/BakeryDining';
import LocalBarIcon from '@mui/icons-material/LocalBar';

const UseCasesSection = () => {
  const {t} = useTranslation();
  const theme = useTheme();

  const useCases = [
    {
      icon: RestaurantIcon,
      title: t('Small restaurants'),
      description: t('Perfect for family-owned restaurants and local eateries. Let diners browse your menu and order from their table.'),
      benefits: [
        t('Reduce wait staff workload'),
        t('Serve more tables efficiently'),
        t('Update menu instantly'),
      ],
    },
    {
      icon: StorefrontIcon,
      title: t('Market kiosks'),
      description: t('Ideal for food stalls and market vendors. Accept orders while you cook, no cash handling needed.'),
      benefits: [
        t('Queue-free ordering'),
        t('Digital payment tracking'),
        t('Manage multiple locations'),
      ],
    },
    {
      icon: LocalCafeIcon,
      title: t('Coffee shops'),
      description: t('Great for cafes and coffee bars. Customers order and pay while waiting, you focus on making great coffee.'),
      benefits: [
        t('Speed up morning rush'),
        t('Loyalty rewards for regulars'),
        t('Track popular items'),
      ],
    },
    {
      icon: IcecreamIcon,
      title: t('Ice cream parlors'),
      description: t('Perfect for ice cream shops and dessert places. Visual menus with photos increase upsells.'),
      benefits: [
        t('Showcase flavors with photos'),
        t('Seasonal menu updates'),
        t('Handle peak hours easily'),
      ],
    },
    {
      icon: BakeryDiningIcon,
      title: t('Bakeries'),
      description: t('Excellent for bakeries and pastry shops. Take pre-orders and reduce morning queues.'),
      benefits: [
        t('Pre-order for pickup'),
        t('Display daily specials'),
        t('Allergen information'),
      ],
    },
    {
      icon: LocalBarIcon,
      title: t('Bars & pubs'),
      description: t('Works great for bars and nightlife venues. Let customers order drinks from anywhere in the venue.'),
      benefits: [
        t('Order from tables or bar'),
        t('Happy hour automation'),
        t('Age verification support'),
      ],
    },
  ];

  return (
    <Box
      sx={{
        py: {xs: 8, md: 12},
        bgcolor: 'background.default',
        position: 'relative',
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{textAlign: 'center', mb: 8}}>
          <Typography
            variant="overline"
            sx={{
              color: 'primary.main',
              fontWeight: 600,
              letterSpacing: '0.1em',
              fontSize: '0.875rem',
            }}
          >
            {t("USE CASES")}
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontSize: {xs: '2rem', md: '3rem'},
              fontWeight: 700,
              mt: 1,
              mb: 2,
              color: 'text.primary',
            }}
          >
            {t("Built for every business type")}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: '1.125rem',
              color: 'text.secondary',
              maxWidth: '700px',
              mx: 'auto',
            }}
          >
            {t("Whether you run a restaurant, kiosk, or boutique shop — QRseller adapts to your needs")}
          </Typography>
        </Box>

        {/* Use Cases Grid */}
        <Grid container spacing={4}>
          {useCases.map((useCase, index) => {
            const IconComponent = useCase.icon;
            return (
              <Grid item xs={12} md={6} key={index}>
                <Box
                  sx={{
                    height: '100%',
                    p: 4,
                    borderRadius: '16px',
                    bgcolor: 'background.paper',
                    border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: `0 12px 28px ${alpha(theme.palette.primary.main, 0.15)}`,
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  {/* Icon & Title */}
                  <Box sx={{display: 'flex', alignItems: 'center', mb: 2}}>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '12px',
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2,
                      }}
                    >
                      <IconComponent
                        sx={{
                          fontSize: 28,
                          color: 'primary.main',
                        }}
                      />
                    </Box>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        color: 'text.primary',
                      }}
                    >
                      {useCase.title}
                    </Typography>
                  </Box>

                  {/* Description */}
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'text.secondary',
                      lineHeight: 1.7,
                      mb: 3,
                    }}
                  >
                    {useCase.description}
                  </Typography>

                  {/* Benefits List */}
                  <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                    {useCase.benefits.map((benefit, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                            fontWeight: 500,
                          }}
                        >
                          {benefit}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>

        {/* Bottom CTA */}
        <Box
          sx={{
            mt: 8,
            textAlign: 'center',
            p: 6,
            borderRadius: '20px',
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 2,
              color: 'text.primary',
            }}
          >
            {t("Don't see your business type?")}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              mb: 1,
            }}
          >
            {t("QRseller works for any business that serves customers. Contact us to learn how we can help you.")}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default UseCasesSection;
